import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';
import { loadStripe, type Stripe } from '@stripe/stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { CreditCard, Loader2Icon } from 'lucide-react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';

import {
  isPaymentMethodAllowed,
  type OnlinePaymentMethod,
  usePaymentMethodOptions,
} from '~entities/payment/api/get-allowed-payment-methods';
import {
  formatEnrollmentSavedMethodLabel,
  pickEnrollmentCatalogDefaultMethod,
  toOnlinePaymentMethod,
  useGetEnrollmentPaymentMethods,
} from '~entities/payment/api/get-enrollment-payment-methods';
import { useGetStudioDetails } from '~entities/studios/api/get-studio-details';

import { useGetCurrency } from '~shared/api/currency-api';
import { KEYS } from '~shared/api/query-keys';
import { ManageParentSquarePaymentMethodsDialog } from '~shared/components/manage-parent-square-payment-methods-dialog';
import { Button } from '~shared/shadcn/ui/button';
import { Checkbox } from '~shared/shadcn/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '~shared/shadcn/ui/dialog';
import { Label } from '~shared/shadcn/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '~shared/shadcn/ui/select';
import { getErrorMessage } from '~shared/utils/error-handler';

interface AddStudentToEntityResponse {
  status: number;
  message: string;
  url?: string;
  clientSecret?: string;
  stripePublicApiKey?: string;
  /** Stripe Connect: connected account ID (acct_xxx) for operating on behalf of studio */
  stripeAccount?: string;
}

interface PaymentConfirmationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (
    paymentMethod: OnlinePaymentMethod | null,
    skipPaymentMethod?: boolean,
    paymentMethodId?: string | null,
  ) => Promise<AddStudentToEntityResponse | null>;
  isLoading: boolean;
  data: {
    student: {
      _id: string;
      name: string;
      parentId: {
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
      };
      entities: Array<{
        enrollmentId?: {
          _id: string;
          title: string;
        };
        eventId?: {
          _id: string;
          title: string;
        };
        /** Only active/scheduled block re-enroll (term_ended may remain on student). */
        subscriptionStatus?: string;
      }>;
    };
    entity: {
      id: string;
      title: string;
      type: 'class' | 'event';
      studioId: string;
    };
    fees: Array<{
      label: string;
      amount: number;
    }>;
  };
  capacityInfo?: {
    remainingSeats: number;
    maxSize: number;
    isFull: boolean;
  };
  isAdmin?: boolean;
}

type DialogMode = 'confirm' | 'missingPayment' | 'addPayment';
interface StripeSetupConfig {
  clientSecret: string;
  publishableKey: string;
  stripeAccount?: string;
}

const PaymentForm = ({
  onClose,
  onSuccess,
  disableLink = true,
}: {
  onClose: () => void;
  onSuccess: () => Promise<void>;
  disableLink?: boolean;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!stripe || !elements) return;

    setIsSubmitting(true);
    try {
      const { error } = await stripe.confirmSetup({
        elements,
        confirmParams: {
          return_url: window.location.href,
        },
        redirect: 'if_required',
      });
      if (error) {
        toast.error(error.message);
      } else {
        toast.success('Payment method added successfully');
        await onSuccess();
      }
    } catch (err) {
      console.error('Error in handleSubmit:', err);
      toast.error('Failed to add payment method');
    }
    setIsSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit} className='space-y-4'>
      <PaymentElement
        options={
          disableLink
            ? ({ wallets: { link: 'never' } } as Record<string, unknown>)
            : undefined
        }
      />
      <div className='flex justify-end gap-2'>
        <Button type='button' variant='outline' onClick={onClose}>
          Cancel
        </Button>
        <Button type='submit' disabled={isSubmitting || !stripe}>
          {isSubmitting ? (
            <Loader2Icon className='animate-spin' />
          ) : (
            'Save Card'
          )}
        </Button>
      </div>
    </form>
  );
};

export const PaymentConfirmationDialog = ({
  isOpen,
  onClose,
  onConfirm,
  data,
  isLoading,
  capacityInfo,
  isAdmin = false,
}: PaymentConfirmationDialogProps) => {
  const queryClient = useQueryClient();
  const { data: currencyDetails } = useGetCurrency();
  const currencySymbol = currencyDetails?.symbol ?? '$';

  const [mode, setMode] = useState<DialogMode>('confirm');
  const [stripeConfig, setStripeConfig] = useState<StripeSetupConfig | null>(
    null,
  );

  const [stripeInstance, setStripeInstance] = useState<Stripe | null>(null);
  const [managePaymentMethodsOpen, setManagePaymentMethodsOpen] =
    useState(false);
  const [skipPaymentMethod, setSkipPaymentMethod] = useState(false);
  const [paymentMethod, setPaymentMethod] =
    useState<OnlinePaymentMethod | null>(null);
  const [resolvedPaymentMethodId, setResolvedPaymentMethodId] = useState<
    string | null
  >(null);

  const { data: studioDetails } = useGetStudioDetails();
  const isV2Enrollment = studioDetails?.isV2 === true;
  const parentId = data.student.parentId._id;
  const locationId = studioDetails?.locationId ?? '';
  const { paymentOptions, allowedMethods } =
    usePaymentMethodOptions(locationId);
  const disableLinkWallet = !isPaymentMethodAllowed(allowedMethods, 'link');
  const {
    data: enrollmentPaymentMethods,
    isLoading: isLoadingEnrollmentPms,
  } = useGetEnrollmentPaymentMethods({
    parentId,
    enabled: isOpen && isV2Enrollment && !!parentId,
  });

  const isAlreadyEnrolled = data.student.entities.some((entity) => {
    const matchesEntity =
      entity.enrollmentId?._id === data.entity.id ||
      entity.eventId?._id === data.entity.id;
    if (!matchesEntity) return false;
    const status = entity.subscriptionStatus;
    // Absent status (legacy payloads) still treated as enrolled.
    if (!status) return true;
    return status === 'active' || status === 'scheduled' || status === 'free';
  });

  useEffect(() => {
    const initStripe = async () => {
      if (mode === 'addPayment' && stripeConfig?.publishableKey) {
        const stripe = await loadStripe(
          stripeConfig.publishableKey,
          stripeConfig.stripeAccount
            ? { stripeAccount: stripeConfig.stripeAccount }
            : undefined,
        );
        setStripeInstance(stripe);
        if (!stripe) {
          toast.error('Failed to load the payment form');
        }
      }
    };
    void initStripe();
  }, [mode, stripeConfig?.publishableKey, stripeConfig?.stripeAccount]);

  useEffect(() => {
    if (!isOpen || !isV2Enrollment || skipPaymentMethod) return;
    if (resolvedPaymentMethodId) {
      const stillPresent = enrollmentPaymentMethods?.savedMethods.some(
        (method) => method.id === resolvedPaymentMethodId,
      );
      if (stillPresent) return;
    }
    const defaultMethod = pickEnrollmentCatalogDefaultMethod(
      enrollmentPaymentMethods,
    );
    if (!defaultMethod) {
      setResolvedPaymentMethodId(null);
      setPaymentMethod(null);
      return;
    }
    setResolvedPaymentMethodId(defaultMethod.id);
    setPaymentMethod(toOnlinePaymentMethod(defaultMethod.type) ?? null);
  }, [
    enrollmentPaymentMethods,
    isOpen,
    isV2Enrollment,
    resolvedPaymentMethodId,
    skipPaymentMethod,
  ]);

  const invalidateSavedPaymentMethods = () => {
    void queryClient.invalidateQueries({
      queryKey: [KEYS.ENROLLMENT_PAYMENT_METHODS, parentId],
    });
    void queryClient.invalidateQueries({ queryKey: [KEYS.CARD] });
  };

  const closeAndReset = () => {
    setMode('confirm');
    setStripeConfig(null);
    setStripeInstance(null);
    setSkipPaymentMethod(false);
    setPaymentMethod(null);
    setResolvedPaymentMethodId(null);
    onClose();
  };

  const openAddPaymentModal = () => {
    if (!stripeConfig) return;
    setMode('addPayment');
  };

  const handleConfirm = async () => {
    try {
      if (!skipPaymentMethod) {
        if (isV2Enrollment) {
          if (!resolvedPaymentMethodId || !paymentMethod) {
            toast.error('Please select a saved payment method');
            return;
          }
        } else if (!paymentMethod) {
          toast.error('Please select a payment method');
          return;
        }
      }
      const response = await onConfirm(
        skipPaymentMethod ? null : paymentMethod,
        skipPaymentMethod,
        skipPaymentMethod ? null : resolvedPaymentMethodId,
      );
      if (!response) {
        // API failures throw; the mutation onError already toasts the backend message.
        return;
      }
      if (
        response?.message === 'Payment method not found' &&
        response.clientSecret &&
        response.stripePublicApiKey
      ) {
        setStripeConfig({
          clientSecret: response.clientSecret,
          publishableKey: response.stripePublicApiKey,
          stripeAccount: response.stripeAccount,
        });
        setMode('missingPayment');
        return;
      }
      closeAndReset();
    } catch (error) {
      console.error('Error in handleConfirm:', error);
      // Axios enroll failures are toasted in useAddStudentToEntity onError with the
      // backend message. Do not invent a second frontend fallback here.
      if (!isAxiosError(error)) {
        toast.error(getErrorMessage(error));
      }
    }
  };

  const handleDialogClose = (open: boolean) => {
    if (!open) {
      closeAndReset();
    }
  };

  const handleUpdateCard = () => {
    setManagePaymentMethodsOpen(true);
  };

  const renderPaymentMethodPicker = () => {
    if (isV2Enrollment) {
      if (isLoadingEnrollmentPms) {
        return (
          <p className='flex items-center gap-2 text-sm text-muted-foreground'>
            <Loader2Icon className='size-3.5 animate-spin' />
            Loading saved payment methods…
          </p>
        );
      }

      if ((enrollmentPaymentMethods?.savedMethods.length ?? 0) > 0) {
        return (
          <div className='space-y-2'>
            <p className='text-sm font-medium text-muted-foreground'>
              Saved payment methods
            </p>
            <Select
              value={resolvedPaymentMethodId ?? ''}
              disabled={skipPaymentMethod}
              onValueChange={(id) => {
                const selected =
                  enrollmentPaymentMethods?.savedMethods.find(
                    (method) => method.id === id,
                  );
                if (!selected) return;
                setSkipPaymentMethod(false);
                setResolvedPaymentMethodId(selected.id);
                setPaymentMethod(toOnlinePaymentMethod(selected.type) ?? null);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder='Select a saved payment method' />
              </SelectTrigger>
              <SelectContent>
                {enrollmentPaymentMethods?.savedMethods.map((method) => (
                  <SelectItem key={method.id} value={method.id}>
                    {formatEnrollmentSavedMethodLabel(method)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        );
      }

      return (
        <p className='text-sm text-muted-foreground'>
          No saved payment methods for this parent. Save a card first, or check
          “No payment method needed”.
        </p>
      );
    }

    return (
      <div className='flex flex-wrap gap-2'>
        {paymentOptions
          .filter((option) => option.show)
          .map((option) => (
            <Button
              key={option.method}
              variant={
                paymentMethod === option.method ? 'default' : 'outline'
              }
              onClick={() => {
                setPaymentMethod(option.method);
              }}
              disabled={skipPaymentMethod}
              className='flex-shrink-0'
            >
              {option.label}
            </Button>
          ))}
      </div>
    );
  };

  return (
    <>
      <Dialog open={isOpen} onOpenChange={handleDialogClose}>
        <DialogContent className='sm:max-w-[550px]'>
          {mode === 'addPayment' && stripeConfig && stripeInstance ? (
            <Elements
              stripe={stripeInstance}
              options={{ clientSecret: stripeConfig.clientSecret }}
            >
              <PaymentForm
                onClose={closeAndReset}
                onSuccess={() => handleConfirm()}
                disableLink={disableLinkWallet}
              />
            </Elements>
          ) : mode === 'missingPayment' ? (
            <>
              <DialogHeader>
                <DialogTitle>No Card on File</DialogTitle>
              </DialogHeader>

              <div className='space-y-3'>
                <p>
                  This parent does not have a payment method on file for this
                  enrollment.
                </p>
                <p className='text-sm text-muted-foreground'>
                  Add a payment method now, or cancel and ask the parent to
                  update their payment details.
                </p>
              </div>

              <DialogFooter className='flex flex-wrap gap-2 pt-4'>
                <Button
                  variant='outline'
                  onClick={closeAndReset}
                  className='flex-shrink-0'
                >
                  Cancel
                </Button>
                <Button onClick={openAddPaymentModal} className='flex-shrink-0'>
                  Add Payment
                </Button>
              </DialogFooter>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Confirm Payment</DialogTitle>
              </DialogHeader>

              <div className='space-y-4'>
                <p className='text-lg'>
                  {skipPaymentMethod
                    ? 'Are you sure you want to enroll this student without collecting payment now?'
                    : 'Are you sure you want to charge this parent:'}
                </p>
                <div className='space-y-2'>
                  <div className='space-y-2'>
                    {renderPaymentMethodPicker()}
                    <div className='flex flex-wrap gap-2'>
                      <Button
                        variant='outline'
                        onClick={handleUpdateCard}
                        disabled={skipPaymentMethod}
                        className='flex-shrink-0'
                      >
                        <CreditCard className='mr-2 size-4' />
                        Update payment details
                      </Button>
                    </div>
                  </div>
                  {!skipPaymentMethod &&
                    (isV2Enrollment
                      ? !resolvedPaymentMethodId
                      : !paymentMethod) && (
                      <p className='text-sm text-red-500'>
                        {isV2Enrollment
                          ? 'Please select a saved payment method'
                          : 'Please select a payment method'}
                      </p>
                    )}
                </div>

                <div className='space-y-2'>
                  <p>
                    👤 Parent:{' '}
                    {data.student.parentId.firstName +
                      ' ' +
                      data.student.parentId.lastName}{' '}
                  </p>
                  <p>📧 Email: {data.student.parentId.email}</p>
                </div>

                <p>
                  For {data.student.name}&apos;s enrollment in the{' '}
                  {data.entity.title}?
                </p>

                <div className='space-y-2'>
                  <p className='font-medium'>💰 Fees Breakdown:</p>
                  <div className='space-y-1 pl-4'>
                    {data.fees.map((fee) => (
                      <p key={fee.label}>
                        {fee.label}: {currencySymbol}
                        {fee.amount.toFixed(2)}
                      </p>
                    ))}
                  </div>
                </div>

                <div className='flex items-start space-x-2'>
                  <Checkbox
                    className='mt-0.5'
                    id='event-skip-payment-method'
                    checked={skipPaymentMethod}
                    onCheckedChange={(checked) => {
                      if (checked === true) {
                        setPaymentMethod(null);
                        setResolvedPaymentMethodId(null);
                      } else if (isV2Enrollment) {
                        const defaultMethod =
                          pickEnrollmentCatalogDefaultMethod(
                            enrollmentPaymentMethods,
                          );
                        if (defaultMethod) {
                          setResolvedPaymentMethodId(defaultMethod.id);
                          setPaymentMethod(
                            toOnlinePaymentMethod(defaultMethod.type) ?? null,
                          );
                        }
                      }
                      setSkipPaymentMethod(checked === true);
                    }}
                  />
                  <Label
                    htmlFor='event-skip-payment-method'
                    className='text-sm'
                  >
                    No payment method needed; the parent will pay the
                    outstanding balance later.
                  </Label>
                </div>

                {isAlreadyEnrolled && (
                  <p className='font-medium text-red-500'>
                    Student is already enrolled in &quot;{data.entity.title}
                    &quot;
                  </p>
                )}

                {capacityInfo?.isFull && (
                  <div className='rounded-lg border-2 border-yellow-500 bg-yellow-50 p-4'>
                    <p className='font-semibold text-yellow-800'>
                      ⚠️ Event is Full
                    </p>
                    <p className='mt-1 text-sm text-yellow-700'>
                      This event has reached its maximum capacity (
                      {capacityInfo.maxSize} students). Current enrollment:{' '}
                      {capacityInfo.maxSize - capacityInfo.remainingSeats}/
                      {capacityInfo.maxSize}
                    </p>
                    {isAdmin && (
                      <p className='mt-2 text-sm font-medium text-yellow-800'>
                        As an admin, you can override this limit, but this will
                        exceed the event&apos;s capacity.
                      </p>
                    )}
                  </div>
                )}
              </div>

              <DialogFooter className='flex flex-wrap gap-2 pt-4'>
                <Button
                  variant='outline'
                  onClick={closeAndReset}
                  className='flex-shrink-0'
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleConfirm}
                  disabled={isAlreadyEnrolled || isLoading}
                  className='flex-shrink-0'
                >
                  {isLoading ? (
                    <Loader2Icon className='animate-spin' />
                  ) : (
                    'Yes, Confirm'
                  )}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <ManageParentSquarePaymentMethodsDialog
        open={managePaymentMethodsOpen}
        onOpenChange={(open) => {
          setManagePaymentMethodsOpen(open);
          if (!open) {
            invalidateSavedPaymentMethods();
          }
        }}
        parentId={data.student.parentId._id}
        parentEmail={data.student.parentId.email}
      />
    </>
  );
};
